// src/components/agent/IdleTimeout.js
// Idle-timeout enforcement for the agent CRM:
//   - 15 min idle  → branded warning popup with 5:00 countdown
//   - 19 min idle  → popup becomes urgent/blocking for the final 60 seconds
//   - 20 min idle  → auto-sign-out (sessionStorage flag tells AgentLogin to show "session expired")
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';

const TOTAL_MS    = 20 * 60 * 1000; // logout at 20 min idle
const WARNING_MS  = 15 * 60 * 1000; // show popup at 15 min idle (5 min remaining)
const BLOCKING_MS = 19 * 60 * 1000; // becomes urgent/blocking at 19 min (60 sec remaining)
export const IDLE_FLAG = 'homeiqa_idle_signout';

export default function IdleTimeout() {
  const [warnVisible, setWarnVisible] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(300);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setWarnVisible(false);
    setIsBlocking(false);
    setSecondsLeft(300);
  }, []);

  // Track user activity — but only while the warning isn't shown.
  // Once the popup is up, activity NO LONGER resets the timer; only the button does.
  useEffect(() => {
    if (warnVisible) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, bump, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, bump));
  }, [warnVisible]);

  // 1Hz tick: drive the warning, blocking phase, and sign-out.
  useEffect(() => {
    const t = setInterval(async () => {
      const idle = Date.now() - lastActivityRef.current;

      if (idle >= TOTAL_MS) {
        sessionStorage.setItem(IDLE_FLAG, '1');
        try { await signOut(auth); } catch {}
        // AgentRoute observes the auth change and routes us to /agent/login.
        return;
      }

      if (idle >= WARNING_MS) {
        setWarnVisible(true);
        setIsBlocking(idle >= BLOCKING_MS);
        setSecondsLeft(Math.max(0, Math.ceil((TOTAL_MS - idle) / 1000)));
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  if (!warnVisible) return null;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div style={isBlocking ? { ...s.overlay, ...s.overlayBlocking } : s.overlay} role="dialog" aria-modal="true">
      <div style={isBlocking ? { ...s.modal, ...s.modalBlocking } : s.modal}>
        <div style={isBlocking ? { ...s.eyebrow, ...s.eyebrowBlocking } : s.eyebrow}>
          {isBlocking ? 'Final warning' : 'Inactivity warning'}
        </div>
        <h2 style={s.title}>
          {isBlocking
            ? <>You're about to be <em style={s.em}>signed&nbsp;out</em>.</>
            : <>Are you still <em style={s.em}>there</em>?</>}
        </h2>

        <div style={isBlocking ? { ...s.countdown, ...s.countdownBlocking } : s.countdown} aria-live="polite">
          {mm}<span style={s.colon}>:</span>{ss}
        </div>

        <p style={s.body}>
          {isBlocking
            ? 'For your security, you’ll be signed out in less than a minute. Click below to stay signed in.'
            : 'You’ll be automatically signed out after 20 minutes of inactivity. Click below to reset the timer and stay signed in.'}
        </p>

        <button onClick={resetTimer} style={isBlocking ? { ...s.btn, ...s.btnBlocking } : s.btn}>
          Reset timer
        </button>

        <div style={s.hint}>
          {isBlocking ? 'Only this button will keep you signed in now.' : 'Tip: any action below resets the timer normally — except while this popup is open.'}
        </div>
      </div>
    </div>
  );
}

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const CREAM = '#FAF7F2';
const WHITE = '#FFFFFF';
const MID = '#5A5A5A';
const LIGHT = '#9A9A9A';
const ALERT = '#B91C1C';

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(2px)' },
  overlayBlocking: { background: 'rgba(74,18,32,0.78)' },
  modal: { background: WHITE, borderRadius: 10, padding: '32px 36px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.4)', border: `1px solid ${GOLD}`, fontFamily: "'DM Sans', sans-serif" },
  modalBlocking: { borderColor: ALERT, boxShadow: `0 0 0 4px rgba(185,28,28,0.18), 0 24px 64px rgba(0,0,0,0.5)` },
  eyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, borderBottom: `1px solid ${GOLD}`, paddingBottom: 4, display: 'inline-block', marginBottom: 14 },
  eyebrowBlocking: { color: ALERT, borderBottomColor: ALERT },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 500, color: BURG_DK, lineHeight: 1.2, margin: '0 0 18px', letterSpacing: '-0.3px' },
  em: { fontStyle: 'italic', color: BURG },
  countdown: { fontFamily: "'Cormorant Garamond', serif", fontSize: 56, fontWeight: 600, color: BURG_DK, letterSpacing: '0.05em', background: CREAM, padding: '14px 24px', borderRadius: 6, display: 'inline-block', marginBottom: 18, fontVariantNumeric: 'tabular-nums' },
  countdownBlocking: { color: ALERT, background: '#FEF2F2', border: `1px solid #FECACA`, animation: 'pulse 1s ease-in-out infinite' },
  colon: { opacity: 0.4, margin: '0 4px' },
  body: { fontSize: 13, color: MID, lineHeight: 1.6, marginBottom: 22, marginTop: 0 },
  btn: { background: BURG, color: WHITE, border: 'none', borderRadius: 4, padding: '13px 28px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", width: '100%', transition: 'background 0.15s ease' },
  btnBlocking: { background: ALERT },
  hint: { fontSize: 11, color: LIGHT, marginTop: 12, lineHeight: 1.5 }
};
